import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Copy, Link2 } from 'lucide-react';
import { api } from '../../../services/api';
import { useWorkspace } from '../WorkspaceContext';
import { DEFAULT_WORKSPACE_PATH } from '../navigation';
import { finalizeAlias, sanitizeAliasInput } from '../utils/alias';
import WeeklyHoursEditor from '../components/WeeklyHoursEditor';
import { DEFAULT_WORKING_HOURS } from './manage/ManageHours';

const STEPS = ['business', 'referral', 'hours', 'username', 'done'];
const VISIBLE_STEPS = 4;

const Onboarding = () => {
  const { business, loading: workspaceLoading, refresh } = useWorkspace();
  const navigate = useNavigate();
  const [step, setStep] = useState('business');
  const [options, setOptions] = useState({ industries: [], referralSources: [] });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [aliasStatus, setAliasStatus] = useState({ state: 'idle', message: '' });

  const [form, setForm] = useState({
    businessName: '',
    industry: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    about: '',
    referralSource: '',
    referralOther: '',
    alias: '',
    workingHours: DEFAULT_WORKING_HOURS,
  });

  const bookingOrigin =
    typeof window !== 'undefined' ? `${window.location.origin}/book/` : '/book/';
  const previewAlias = finalizeAlias(form.alias);
  const bookingPreview = previewAlias ? `${bookingOrigin}${previewAlias}` : null;

  useEffect(() => {
    if (!workspaceLoading && business?.onboardingComplete) {
      navigate(DEFAULT_WORKSPACE_PATH, { replace: true });
    }
  }, [business, workspaceLoading, navigate]);

  useEffect(() => {
    if (!business) return;
    setForm((prev) => ({
      ...prev,
      businessName: prev.businessName || business.name || '',
      industry: prev.industry || business.industry || '',
      address: prev.address || business.address || '',
      city: prev.city || business.city || '',
      country: prev.country || business.country || '',
      phone: prev.phone || business.phone || '',
      about: prev.about || business.about || '',
      alias: prev.alias || business.slug || '',
    }));
  }, [business]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await api.getOnboardingOptions();
      if (!cancelled && result.data) {
        setOptions({
          industries: result.data.industries || [],
          referralSources: result.data.referralSources || [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (step !== 'username') return;
    const alias = finalizeAlias(form.alias);
    if (!alias || alias === business?.slug) {
      setAliasStatus({
        state: alias && alias === business?.slug ? 'available' : 'idle',
        message: alias && alias === business?.slug ? 'This is your current link' : '',
      });
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setAliasStatus({ state: 'checking', message: 'Checking availability…' });
      const result = await api.checkBookingAlias(alias);
      if (cancelled) return;
      if (result.error) {
        setAliasStatus({ state: 'error', message: result.error });
        return;
      }
      if (result.data?.available) {
        setAliasStatus({
          state: 'available',
          message: 'That username is available',
        });
      } else {
        setAliasStatus({
          state: 'taken',
          message: result.data?.error || 'That username is already taken',
        });
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.alias, step, business?.slug]);

  const stepIndex = STEPS.indexOf(step);

  const canContinueBusiness = useMemo(
    () =>
      form.businessName.trim().length >= 2 &&
      Boolean(form.industry) &&
      form.address.trim().length > 0 &&
      form.city.trim().length > 0 &&
      form.country.trim().length > 0 &&
      form.phone.trim().length > 0 &&
      form.about.trim().length > 0,
    [form.businessName, form.industry, form.address, form.city, form.country, form.phone, form.about]
  );

  const canContinueReferral = Boolean(form.referralSource) &&
    (form.referralSource !== 'other' || form.referralOther.trim().length > 0);

  const canContinueUsername =
    finalizeAlias(form.alias).length >= 3 &&
    (aliasStatus.state === 'available' || finalizeAlias(form.alias) === business?.slug);

  const copyLink = async () => {
    if (!bookingPreview) return;
    try {
      await navigator.clipboard.writeText(bookingPreview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy link');
    }
  };

  const submitOnboarding = async () => {
    setSaving(true);
    setError('');
    const result = await api.completeOnboarding({
      businessName: form.businessName.trim(),
      industry: form.industry,
      address: form.address.trim(),
      city: form.city.trim(),
      country: form.country.trim(),
      phone: form.phone.trim(),
      about: form.about.trim(),
      referralSource: form.referralSource,
      referralOther: form.referralOther.trim() || null,
      alias: finalizeAlias(form.alias),
      workingHours: form.workingHours,
    });
    setSaving(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    await refresh();
    setStep('done');
  };

  if (workspaceLoading || !business) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand-100">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      <header className="border-b border-ink-900/10 bg-sand-50">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-5 sm:px-6">
          <Link to="/" className="font-display text-xl font-light tracking-tight">
            Spirara<span className="text-saffron-500">.</span>
          </Link>
          <p className="text-xs uppercase tracking-[0.18em] text-ink-600/60">
            Step {Math.min(stepIndex + 1, VISIBLE_STEPS)} of {VISIBLE_STEPS}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-10 sm:px-6 sm:py-14">
        {error && (
          <div className="mb-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {step === 'business' && (
          <section>
            <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Your business</p>
            <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
              Tell us about the shop
            </h1>
            <p className="mt-3 text-base text-ink-700/80">
              We&rsquo;ll use this to set up your calendar, client book and booking page.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Business name
                </label>
                <input
                  required
                  value={form.businessName}
                  onChange={(e) => setForm((prev) => ({ ...prev, businessName: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Ivory & Coil"
                />
              </div>

              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  What kind of business?
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {options.industries.map((item) => {
                    const selected = form.industry === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, industry: item.id }))}
                        className={`px-4 py-3 text-left text-sm transition-colors ${
                          selected
                            ? 'bg-ink-900 text-sand-50'
                            : 'bg-sand-50 text-ink-800 ring-1 ring-ink-900/10 hover:bg-white'
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Street address <span className="text-saffron-600">*</span>
                </label>
                <input
                  required
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="14 Admiralty Way"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    City <span className="text-saffron-600">*</span>
                  </label>
                  <input
                    required
                    value={form.city}
                    onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                    className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Lagos"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                    Country <span className="text-saffron-600">*</span>
                  </label>
                  <input
                    required
                    value={form.country}
                    onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
                    className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Nigeria"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Business phone <span className="text-saffron-600">*</span>
                </label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="+234…"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Short description <span className="text-saffron-600">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={form.about}
                  onChange={(e) => setForm((prev) => ({ ...prev, about: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  placeholder="Braids, silk press and colour in Lekki"
                />
              </div>
            </div>

            <button
              type="button"
              disabled={!canContinueBusiness}
              onClick={() => {
                setError('');
                setStep('referral');
              }}
              className="group mt-8 inline-flex items-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-40"
            >
              Continue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </section>
        )}

        {step === 'referral' && (
          <section>
            <button
              type="button"
              onClick={() => setStep('business')}
              className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-saffron-600">Almost there</p>
            <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
              How did you hear about us?
            </h1>
            <p className="mt-3 text-base text-ink-700/80">
              This helps us understand what&rsquo;s working for beauty businesses like yours.
            </p>

            <div className="mt-8 grid gap-2 sm:grid-cols-2">
              {options.referralSources.map((item) => {
                const selected = form.referralSource === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, referralSource: item.id }))}
                    className={`px-4 py-3 text-left text-sm transition-colors ${
                      selected
                        ? 'bg-ink-900 text-sand-50'
                        : 'bg-sand-50 text-ink-800 ring-1 ring-ink-900/10 hover:bg-white'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            {form.referralSource === 'other' && (
              <div className="mt-5">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Please tell us more
                </label>
                <input
                  value={form.referralOther}
                  onChange={(e) => setForm((prev) => ({ ...prev, referralOther: e.target.value }))}
                  className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                />
              </div>
            )}

            <button
              type="button"
              disabled={!canContinueReferral}
              onClick={() => {
                setError('');
                setStep('hours');
              }}
              className="group mt-8 inline-flex items-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-40"
            >
              Continue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </section>
        )}

        {step === 'hours' && (
          <section>
            <button
              type="button"
              onClick={() => setStep('referral')}
              className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-saffron-600">Calendar</p>
            <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
              When are you open?
            </h1>
            <p className="mt-3 text-base text-ink-700/80">
              Your booking calendar stays open during these hours. You can block individual times later
              from Calendar whenever you need to close a slot.
            </p>

            <div className="mt-8">
              <WeeklyHoursEditor
                value={form.workingHours}
                onChange={(workingHours) => setForm((prev) => ({ ...prev, workingHours }))}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setError('');
                if (!form.alias && form.businessName) {
                  setForm((prev) => ({
                    ...prev,
                    alias: sanitizeAliasInput(form.businessName),
                  }));
                }
                setStep('username');
              }}
              className="group mt-8 inline-flex items-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
            >
              Continue
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </section>
        )}

        {step === 'username' && (
          <section>
            <button
              type="button"
              onClick={() => setStep('hours')}
              className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ink-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <p className="mt-6 text-xs uppercase tracking-[0.22em] text-saffron-600">Booking link</p>
            <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
              Choose your username
            </h1>
            <p className="mt-3 text-base text-ink-700/80">
              This becomes the link clients use to book you. Hyphens are allowed — for example{' '}
              <span className="font-medium text-ink-900">ivory-and-coil</span>.
            </p>

            <div className="mt-8">
              <label
                htmlFor="onboarding-alias"
                className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
              >
                Username
              </label>
              <div className="flex overflow-hidden bg-white ring-1 ring-inset ring-ink-900/15 focus-within:ring-2 focus-within:ring-saffron-500">
                <span className="hidden flex-none items-center bg-sand-50 px-3 text-sm text-ink-600/60 sm:inline-flex">
                  {bookingOrigin}
                </span>
                <span className="inline-flex flex-none items-center bg-sand-50 px-3 text-sm text-ink-600/60 sm:hidden">
                  /book/
                </span>
                <input
                  id="onboarding-alias"
                  value={form.alias}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, alias: sanitizeAliasInput(e.target.value) }))
                  }
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCorrect="off"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-ink-900 focus:outline-none"
                  placeholder="ivory-and-coil"
                />
              </div>
              {aliasStatus.message && (
                <p
                  className={`mt-3 text-sm ${
                    aliasStatus.state === 'available'
                      ? 'text-ink-700'
                      : aliasStatus.state === 'taken' || aliasStatus.state === 'error'
                        ? 'text-red-700'
                        : 'text-ink-600/70'
                  }`}
                >
                  {aliasStatus.message}
                </p>
              )}
            </div>

            {bookingPreview && (
              <div className="mt-6 bg-ink-900 p-5 text-sand-50">
                <p className="text-xs uppercase tracking-[0.18em] text-saffron-400">Your booking link</p>
                <div className="mt-3 flex items-center gap-3">
                  <Link2 className="h-4 w-4 flex-none text-saffron-400" />
                  <p className="min-w-0 flex-1 truncate text-sm">{bookingPreview}</p>
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  className="mt-4 inline-flex items-center gap-2 bg-saffron-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-ink-900 hover:bg-saffron-400"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            )}

            <button
              type="button"
              disabled={!canContinueUsername || saving}
              onClick={submitOnboarding}
              className="mt-8 inline-flex items-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Finish setup'}
            </button>
          </section>
        )}

        {step === 'done' && (
          <section className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center bg-ink-900">
              <Check className="h-7 w-7 text-saffron-400" />
            </div>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tight sm:text-5xl">
              You&rsquo;re ready
            </h1>
            <p className="mx-auto mt-3 max-w-md text-ink-700/80">
              {form.businessName} is set up. Share your booking link, add services in Manage, and
              start taking appointments on the calendar.
            </p>
            {bookingPreview && (
              <p className="mt-6 break-all text-sm font-medium text-ink-900">{bookingPreview}</p>
            )}
            <button
              type="button"
              onClick={() => navigate(DEFAULT_WORKSPACE_PATH, { replace: true })}
              className="group mt-8 inline-flex items-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400"
            >
              Open workspace
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default Onboarding;
